import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import PersonIcon from '@mui/icons-material/Person';
import Avatar from '@mui/material/Avatar';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FlagIcon from '@mui/icons-material/Flag';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import BugReportIcon from '@mui/icons-material/BugReport';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MicIcon from '@mui/icons-material/Mic';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import SessionTimer from './SessionTimer';

const SETTING_LABELS = {
  outpatient_clinic: 'Outpatient Clinic',
  emergency_department: 'Emergency Department',
  inpatient_ward: 'Inpatient Ward',
  community_mental_health: 'Community MH',
  gp_surgery: 'GP Surgery',
  crisis_team: 'Crisis Team',
};

const formatSetting = (setting) => SETTING_LABELS[setting] || setting || 'Clinical Setting';

const SessionHeader = ({
  scenarioBrief,
  timeRemainingSeconds,
  timerWarning,
  totalTurns,
  onConclude,
  onAbandon,
  onRequestAssessment,
  onSuggestGoal,
  onToggleNotes,
  notesOpen,
  availableAssessments = [],
  assessmentsUsed = [],
  // Session interaction mode — replaces the legacy autoplay/voice toggles.
  // sessionMode: 'chat' | 'voice' | 'call'
  //   chat  → text only; user manually plays AI replies via the message
  //           speaker icon
  //   voice → text input + auto-play replies (TTS streams as they arrive)
  //   call  → Advanced Voice Mode / live conversation modal
  sessionMode = 'chat',
  onSessionModeChange,
  voiceModeEligible = false,
  onReportIssue,
  isTherapistPractice = false,
  isPaused = false,
  onTogglePause,
  pauseInFlight = false,
}) => {
  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      {/* Left: scenario info (filtered by visibility level) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {scenarioBrief?.personaImageUrl ? (
          <Avatar
            src={scenarioBrief.personaImageUrl}
            alt="Patient"
            variant="rounded"
            sx={{ width: 44, height: 44 }}
          />
        ) : (
          <PersonIcon color="primary" />
        )}
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {isTherapistPractice ? 'Practice Patient' : 'Training Patient'}
          </Typography>
          <Typography variant="subtitle1" fontWeight={600}>
            {scenarioBrief?.visibilityLevel === 'blind'
              ? `New Patient — ${formatSetting(scenarioBrief?.setting)}`
              : scenarioBrief?.name || 'Training Session'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {scenarioBrief?.visibilityLevel === 'blind'
              ? formatSetting(scenarioBrief?.setting)
              : scenarioBrief?.visibilityLevel === 'referral_only'
                ? `${formatSetting(scenarioBrief?.setting)}${scenarioBrief?.referralSource ? ` | Referral: ${scenarioBrief.referralSource}` : ''}`
                : `${formatSetting(scenarioBrief?.setting)}${scenarioBrief?.presentingComplaint ? ` | ${scenarioBrief.presentingComplaint}` : ''}`
            }
          </Typography>
        </Box>
      </Box>

      {/* Centre: timer (when timed) */}
      {timeRemainingSeconds != null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SessionTimer
            secondsRemaining={timeRemainingSeconds}
            warning={timerWarning}
          />
          {isPaused && (
            <Chip size="small" color="warning" label="Paused" variant="outlined" />
          )}
        </Box>
      )}

      {/* Right: action buttons */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          Turns: {totalTurns}
        </Typography>

        {/* Session mode selector — chat / voice message / call */}
        {onSessionModeChange && (
          <ToggleButtonGroup
            value={sessionMode}
            exclusive
            size="small"
            onChange={(_e, next) => {
              if (next && next !== sessionMode) onSessionModeChange(next);
            }}
            aria-label="session mode"
          >
            <ToggleButton value="chat" aria-label="chat mode">
              <Tooltip title="Chat — type or record; play replies manually">
                <ChatBubbleOutlineIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="voice" aria-label="voice message mode">
              <Tooltip title="Voice messages — type or record; replies auto-play">
                <MicIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="call" aria-label="call mode" disabled={!voiceModeEligible}>
              <Tooltip title={voiceModeEligible ? 'Call — live voice conversation' : 'Call mode not enabled for this scenario'}>
                <span style={{ display: 'inline-flex' }}>
                  <PhoneInTalkIcon fontSize="small" />
                </span>
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {onToggleNotes && (
          <Tooltip title="Clinical Notes">
            <IconButton onClick={onToggleNotes} color={notesOpen ? 'primary' : 'default'}>
              <NoteAltIcon />
            </IconButton>
          </Tooltip>
        )}

        {onReportIssue && (
          <Tooltip title="Report an issue">
            <IconButton onClick={onReportIssue} color="default" size="small">
              <BugReportIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* Hide assessment button in blind mode when blindModeAssessments is 'hidden' */}
        {!(scenarioBrief?.visibilityLevel === 'blind' && scenarioBrief?.blindModeAssessments === 'hidden') && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AssessmentIcon />}
            onClick={onRequestAssessment}
            disabled={Array.isArray(availableAssessments) && availableAssessments.length === 0}
          >
            Request Assessment
          </Button>
        )}

        {onSuggestGoal && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FlagIcon />}
            onClick={onSuggestGoal}
            data-testid="header-suggest-goal"
          >
            Suggest Goal
          </Button>
        )}

        {isTherapistPractice && onTogglePause && (
          <Tooltip title={isPaused ? 'Resume session — clock restarts' : 'Pause session — stops the clock'}>
            <span>
              <IconButton
                onClick={onTogglePause}
                color={isPaused ? 'primary' : 'default'}
                size="small"
                disabled={pauseInFlight}
              >
                {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={onConclude}
        >
          {isTherapistPractice ? 'Finish & Reflect' : 'Conclude Session'}
        </Button>

        {onAbandon && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onAbandon}
          >
            {isTherapistPractice ? 'Cancel Practice' : 'Cancel Session'}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default SessionHeader;
